import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, SearchParamError } from '@tanstack/react-router';
import { FormattedMessage } from 'react-intl';
import { z } from 'zod';
import { getReactQueryOptions, isApiError } from '@_/utils-frontend';
import { publicApiQueryClient } from '#src/api.ts';
import {
  AcceptOrgInviteAsNewUserPage,
  AcceptOrgInvitePage
} from '#src/pages/AcceptOrgInvitePage/index.ts';
import { NotFoundPage } from '#src/pages/errorPages/NotFoundPage.tsx';
import { GenericErrorPage } from '#src/pages/GenericErrorPage/index.ts';

export const Route = createFileRoute('/{-$locale}/email-link_/_wrapper/accept-org-invite')({
  component: RouteComponent,
  errorComponent: ({ error }) => {
    if (error instanceof SearchParamError) {
      return <NotFoundPage />;
    }

    if (isApiError(error) && error.status === 404) {
      return (
        <GenericErrorPage
          heading={
            <FormattedMessage
              id="error.invite.accept-failed"
              defaultMessage="Failed to join business"
            />
          }
          message={
            <FormattedMessage
              id="error.invite.accept-failed.description"
              defaultMessage="This is likely because the link has expired. Please contact the business to resend a new invite."
            />
          }
        />
      );
    }

    throw error;
  },
  validateSearch: z.object({
    inviteId: z.string().trim().nonempty(),
    token: z.string().trim().nonempty()
  })
});

function RouteComponent() {
  const { inviteId, token } = Route.useSearch();

  const inviteQuery = useSuspenseQuery(
    getReactQueryOptions(publicApiQueryClient.customerInvites.get, {
      headers: { authorization: `Bearer ${token}` },
      params: { id: inviteId }
    })
  );

  const invite = inviteQuery.data.data;

  if (invite.userId) {
    return <AcceptOrgInvitePage invite={invite} token={token} />;
  }

  return <AcceptOrgInviteAsNewUserPage invite={invite} token={token} />;
}
